import { Option } from "../types/select";
import { getIconComponent } from "./icons";

/**
 * Normalizes stored data to Option[] format
 * Handles both string arrays and {value, label} object arrays
 */
export function normalizeStoredOptions(stored: any[]): Option[] {
  return stored
    .map((item: any) => {
      if (typeof item === "string") {
        return { value: item, label: item } as Option;
      }
      if (
        item &&
        typeof item === "object" &&
        "value" in item &&
        "label" in item
      ) {
        return {
          value: String(item.value),
          label: String(item.label),
          Icon: item.iconName ? getIconComponent(item.iconName) : undefined,
        } as Option;
      }
      return null;
    })
    .filter(Boolean) as Option[];
}

/**
 * Merges schema options with localStorage options, avoiding duplicates
 */
export function mergeOptionsWithStorage(
  schemaOptions: Option[],
  storageKey: string
): Option[] {
  if (!storageKey || typeof window === "undefined") {
    return schemaOptions;
  }

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return schemaOptions;

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return schemaOptions;

    const storedOptions = normalizeStoredOptions(parsed);
    const existingValues = new Set(schemaOptions.map((o) => o.value));

    for (const opt of storedOptions) {
      if (!existingValues.has(opt.value)) {
        existingValues.add(opt.value);
        schemaOptions = [...schemaOptions, opt];
      }
    }
  } catch (_e) {
    // ignore malformed localStorage values
  }

  return schemaOptions;
}

/**
 * Saves a new option to localStorage and notifies other components
 */
export function saveOptionToStorage(
  inputValue: string,
  storageKey: string
): void {
  if (!storageKey || typeof window === "undefined") return;

  try {
    const raw = window.localStorage.getItem(storageKey);
    const parsed = raw ? JSON.parse(raw) : [];
    const normalized = normalizeStoredOptions(
      Array.isArray(parsed) ? parsed : []
    );

    const existing = new Set(normalized.map((o) => o.value));
    if (!existing.has(inputValue)) {
      normalized.push({ value: inputValue, label: inputValue });
      window.localStorage.setItem(storageKey, JSON.stringify(normalized));

      // Notify same-tab listeners to refresh
      try {
        window.dispatchEvent(
          new CustomEvent("m2f:select-options-updated", {
            detail: { storageKey },
          })
        );
      } catch (_e) {
        // ignore
      }
    }
  } catch (_e) {
    // ignore storage errors
  }
}

/**
 * Creates storage event handler for cross-tab updates
 */
export function createStorageEventHandler(
  storageKey: string,
  schemaOptions: Option[],
  setOptions: (options: Option[]) => void
) {
  return (event: StorageEvent) => {
    if (event.key !== storageKey) return;

    const mergedOptions = mergeOptionsWithStorage(schemaOptions, storageKey);
    setOptions(mergedOptions);
  };
}

/**
 * Creates custom event handler for same-tab updates
 */
export function createCustomEventHandler(
  storageKey: string,
  schemaOptions: Option[],
  setOptions: (options: Option[]) => void
) {
  return (event: any) => {
    if (event?.detail?.storageKey !== storageKey) return;

    const mergedOptions = mergeOptionsWithStorage(schemaOptions, storageKey);
    setOptions(mergedOptions);
  };
}

/**
 * Normalizes survey question choices to Option[] format
 * Handles both string arrays and {value, text} object arrays
 */
export function normalizeQuestionChoices(
  choices: (string | { value: string; text: string; [key: string]: any })[]
): Option[] {
  return choices.map(
    (c: string | { value: string; text: string; [key: string]: any }) => {
      if (typeof c === "string") {
        return {
          value: c,
          label: c,
        };
      }
      return {
        value: c.value,
        label: c.text,
        Icon: c.iconName ? getIconComponent(c.iconName) : undefined,
      };
    }
  );
}

/**
 * Creates a filter function for select options that shows "Other" option only when no other options match
 */
export function createSelectFilterFunction(options: Option[]) {
  return (option: any, inputValue: string) => {
    // If no input value, show all options
    if (!inputValue) {
      return true;
    }

    // Check if the option matches the input (case-insensitive)
    const matchesInput = option.label
      .toLowerCase()
      .includes(inputValue.toLowerCase());

    // If it matches, show it
    if (matchesInput) {
      return true;
    }

    // If it doesn't match, check if it's an "Other" option
    const isOtherOption = option.value.toLowerCase() === "other";

    // Show "Other" option only if no other options match the input
    if (isOtherOption) {
      // Check if any other options match the input
      const hasMatchingOptions = options.some(
        (opt: Option) =>
          opt.value !== option.value &&
          opt.label.toLowerCase().includes(inputValue.toLowerCase())
      );

      // Show "Other" only if no other options match
      return !hasMatchingOptions;
    }

    return false;
  };
}
